import { AUTH_MODE, RESET_PASSWORD } from '../../common/config';
import axiosGolang from 'lib/axios/axios-golang';
import * as actionTypes from '../actions/actionTypes';
import { setError } from './auth';
import {
  getChannelContextAdditionalInfo,
  getChannelContextCode,
  getFirstAuthorizationMode
} from '../../helpers/Utility';
import { loading } from './loanApply';
import { encryptRSA } from 'helpers/rsaEncrypter';

/**
 * @param {Object} consist of:
 * 1. requestBody
 * 2. callback
 */
export const submitNewPin = param => {
  const { callback, typedPin } = param;

  return async (dispatch, getState) => {
    dispatch(loading(true));

    try {
      const { auth } = getState();

      const [encryptedPhone, encryptedReTypePin, encryptedPin] =
        await Promise.all([
          encryptRSA(auth.phoneNo),
          encryptRSA(typedPin),
          encryptRSA(typedPin)
        ]);

      const requestHeader = {
        headers: {
          Authorization: auth.VTtoken
        }
      };

      const requestBody = {
        loginUserId: encryptedPhone,
        passwordDetails: {
          passwordType: AUTH_MODE,
          reTypesignOnPassword: encryptedReTypePin,
          signOnPassword: encryptedPin
        }
      };

      await axiosGolang.post(RESET_PASSWORD, requestBody, requestHeader);

      callback && callback('input-otp');
    } catch (err) {
      if (!err.response) {
        dispatch(setError('connection'));
        return;
      }
      // Phone number already changed
      if (getChannelContextCode(err, 'channel-context') === '4000009') {
        dispatch(setError('have-change-number'));
        return;
      }

      // PIN already used
      if (getChannelContextCode(err, 'channel-context') === '4000008') {
        callback && callback('used');
        dispatch(setForgotPinNewPinAlreadyUsed(true));
        dispatch(setError('already-used'));
        dispatch(loading(false));
        return;
      }

      // Pin repeated ie 123456
      if (getChannelContextCode(err, 'channel-context') === '40000001') {
        callback && callback('repeated');
        return;
      }

      // There's no response from server
      if (getChannelContextCode(err, 'channel-context') === null) {
        dispatch(setError('system'));
        return;
      }

      if (getChannelContextCode(err, 'channel-context') === '200604') {
        dispatch(setError('contact-cs'));
        return;
      }

      // New PIN accepted (before LOAN_CREATED)
      if (getFirstAuthorizationMode(err, 'channel-context') === 'CLGOTP') {
        callback && callback('input-otp');
        return;
      }

      // New PIN accepted (status LOAN_CREATED)
      if (getFirstAuthorizationMode(err, 'channel-context') === 'CLGDC') {
        callback && callback('atm-verification');
        return;
      }

      // Weak PIN
      if (getChannelContextCode(err, 'channel-context') === '4000005') {
        dispatch(setError('weakpin'));
        return;
      }

      if (getChannelContextCode(err, 'channel-context') === '4000203') {
        callback && callback('limit_receive_otp');
        return;
      }
      if (getChannelContextCode(err, 'channel-context') === '4000301') {
        const addInfo = getChannelContextAdditionalInfo(err, 'channel-context');
        const freezeTime = addInfo?.freezeEnd;
        localStorage.setItem('otpFreeze', JSON.stringify({ freezeTime }));
        dispatch(setError('otp_temporary_locked'));
        callback && callback('otp_temporary_locked');
        return;
      }
      //temporary locked due to multiple incorrect pin
      if (getChannelContextCode(err, 'channel-context') === '400502') {
        const addInfo = getChannelContextAdditionalInfo(err, 'channel-context');
        const freezeTime = addInfo?.freezeEnd;
        localStorage.setItem('pinFreeze', JSON.stringify({ freezeTime }));
        dispatch(setError('temporary_locked'));
        dispatch(loading(false));
        return;
      }
      dispatch(setError('system'));
    } finally {
      dispatch(loading(false));
    }
  };
};

/**
 * @param {Object} consist of:
 * 1. requestBody
 * 2. callback
 */
export const submitVerification = param => {
  const { requestBody, callback, failed } = param;

  return (dispatch, getState) => {
    dispatch(loading(true));

    const requestHeader = {
      headers: {
        Authorization: getState().auth.VTtoken
      }
    };

    axiosGolang
      .post(RESET_PASSWORD, requestBody, requestHeader)
      .then(res => {
        return res.status.toString().charAt(0) === '2';
      })
      .then(ok => {
        ok && callback && callback('ok');
      })
      .catch(err => {
        if (!err.response) {
          dispatch(setError('connection'));
          return;
        }

        // There's no response from server
        if (getChannelContextCode(err, 'channel-context') === null) {
          dispatch(setError('system'));
          return;
        }

        // PIN already used before (at least latest 3 times)
        if (getChannelContextCode(err, 'channel-context') === '107302') {
          callback && callback('used');
          return;
        }

        // OTP doesn't match at all
        if (getChannelContextCode(err, 'channel-context') === '4000301') {
          const addInfo = getChannelContextAdditionalInfo(
            err,
            'channel-context'
          );
          const freezeTime = addInfo?.freezeEnd;
          localStorage.setItem('otpFreeze', JSON.stringify({ freezeTime }));
          dispatch(setError('otp_temporary_locked'));
          dispatch(loading(false));
          return;
        }
        if (getChannelContextCode(err, 'channel-context') === '4000300') {
          dispatch(setError('incorrect_otp'));
          failed && failed();
          return;
        }
        //  No record found for the entered information (via ATM verification)
        if (getChannelContextCode(err, 'channel-context') === '211051') {
          callback && callback('not-verified');
          return;
        }
        // OTP expired
        if (getChannelContextCode(err, 'channel-context') === '405203') {
          dispatch(setError('otp-expired'));
          return;
        }
        // Other(s) error
        dispatch(setError('system'));
      })
      .finally(() => {
        dispatch(loading(false));
      });
  };
};

export const setForgotPinNewPinAlreadyUsed = isNewPinAlreadyUsed => {
  return {
    type: actionTypes.SET_FORGOT_PIN_NEW_PIN_ALREADY_USED,
    isNewPinAlreadyUsed: isNewPinAlreadyUsed
  };
};

export const setReTypeSignOnPassword = reTypeSignOnPassword => {
  return {
    type: actionTypes.SET_RETYPE_SIGN_ON_PIN,
    reTypeSignOnPassword: reTypeSignOnPassword
  };
};

export const setSignOnPassword = signOnPassword => {
  return {
    type: actionTypes.SET_TYPE_SIGN_ON_PIN,
    signOnPassword: signOnPassword
  };
};
